import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation } from "@react-navigation/native";
import {
  Wine,
  Coffee,
  Cigarette,
  Wind,
  Package,
  Leaf,
  Droplet,
  Pill,
  Flame,
  Plus,
  type LucideIcon,
} from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSubstances, SubstanceType } from "../SubstancesContext";
import { SUBSTANCE_OPTIONS } from "../options";
import { buildSubstancesNavigationStack } from "../buildSubstancesNavigationStack";
import type { SubstancesStackScreenProps } from "../SubstancesNavigator";

const SUBSTANCE_ICONS: Record<SubstanceType, LucideIcon> = {
  alcohol: Wine,
  coffee: Coffee,
  cigarettes: Cigarette,
  vape: Wind,
  snus: Package,
  cannabis: Leaf,
  cbd: Droplet,
  cocaine: Pill,
  hookah: Flame,
  other: Plus,
};

export function SubstancesSelectScreen() {
  const t = useTranslations();
  const { state, updateState } = useSubstances();
  const navigation =
    useNavigation<
      SubstancesStackScreenProps<"SubstancesSelect">["navigation"]
    >();

  const selected = useMemo(
    () => new Set(state.selectedSubstances || []),
    [state.selectedSubstances],
  );

  const toggleSelection = (substance: SubstanceType) => {
    const newSelection = new Set(selected);
    if (newSelection.has(substance)) {
      newSelection.delete(substance);
    } else {
      newSelection.add(substance);
    }

    const selection = Array.from(newSelection);
    const nextSet = new Set(selection);
    const idsToDelete = new Set(state.substanceIdsToDelete);

    state.substanceDetails.forEach((detail) => {
      if (detail.substance && !nextSet.has(detail.substance) && detail.id) {
        idsToDelete.add(detail.id);
      }
    });

    const detailMap = new Map(
      state.substanceDetails.map((detail) => [detail.substance, detail]),
    );

    const nextDetails = selection.map((type) => {
      const existing = detailMap.get(type);
      if (existing) return existing;
      return {
        id: undefined,
        substance: type,
        frequency: null,
        averageQuantity: null,
        otherFrequency: null,
        startDate: null,
        stopDate: null,
        notes: null,
        other: null,
      };
    });

    const partial = {
      selectedSubstances: selection,
      substanceDetails: nextDetails,
      substanceIdsToDelete: Array.from(idsToDelete),
    };

    const stack = buildSubstancesNavigationStack({ ...state, ...partial });
    const firstDetailIndex = stack.findIndex((route) => {
      if (typeof route === "object") {
        return route.type === "SubstanceDetails";
      }
      return false;
    });

    updateState({
      ...partial,
      navigationStack: stack,
      currentNavigationIndex:
        firstDetailIndex >= 0 ? firstDetailIndex : stack.length - 1,
    });
  };

  const handleSave = () => {
    if (selected.size === 0) return;

    const stack = buildSubstancesNavigationStack(state);
    const firstDetail = stack.find(
      (
        route,
      ): route is { type: "SubstanceDetails"; substance: SubstanceType } =>
        typeof route === "object" && route.type === "SubstanceDetails",
    );

    if (firstDetail) {
      const index = stack.findIndex(
        (route) =>
          typeof route === "object" &&
          route.type === "SubstanceDetails" &&
          route.substance === firstDetail.substance,
      );
      updateState({ navigationStack: stack, currentNavigationIndex: index });
      navigation.navigate("SubstanceDetails", {
        substance: firstDetail.substance,
      });
    } else {
      const completeIndex = stack.findIndex(
        (route) => route === "SubstancesComplete",
      );
      updateState({
        navigationStack: stack,
        currentNavigationIndex: completeIndex >= 0 ? completeIndex : 0,
      });
      navigation.navigate("SubstancesComplete");
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.substances.select_title")}
      subtitle={t("med_history.lifestyle.substances.select_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selected.size === 0}
      saveLabel={t("med_history.lifestyle.continue")}
    >
      <View style={styles.optionsGrid}>
        {SUBSTANCE_OPTIONS.map((option) => {
          const isSelected = selected.has(option.id);
          const Icon = SUBSTANCE_ICONS[option.id];
          if (!Icon) {
            return null;
          }
          return (
            <View key={option.id} style={styles.optionWrapper}>
              <SquareOptionButton
                icon={Icon}
                label={t(option.labelKey)}
                isSelected={isSelected}
                onPress={() => toggleSelection(option.id)}
                iconSize={20}
                containerSize={40}
                labelSize="sm"
              />
            </View>
          );
        })}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});
